import React from 'react';

import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupIcon from '@mui/icons-material/Group';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';

import { Attendee } from '../../../components/AttendeesDialog';
import { Button, Fields } from '../../../components/DetailsModal';
import { EventState } from '../../../components/EventCell/types';
import { ShortStudent } from '../../../services/students/types';
import { Tutorial } from '../../../services/tutorials/types';
import { getStartDatetime } from '../../../services/tutorials/utils';
import addDays from '../../../utils/common/addDays';

export const getTutorialFormFields = (
  isOnlyTeacher: boolean,
): Fields => ({
  course: {
    editable: true,
    type: 'select',
  },
  startDatetime: {
    editable: true,
    type: 'datetime',
  },
  host: {
    label: 'Host',
    editable: !isOnlyTeacher,
    hide: isOnlyTeacher,
    type: 'select',
  },
  capacity: {
    editable: !isOnlyTeacher,
    hide: isOnlyTeacher,
    type: 'number',
  },
  duration: {
    label: 'Duration (mins)',
    editable: !isOnlyTeacher,
    hide: isOnlyTeacher,
    type: 'number',
  },
  description: {
    label: 'Description',
    editable: true,
    type: 'text',
  },
  joinLink: {
    label: 'Zoom link',
    editable: true,
    type: 'text',
  },
  repeat: {
    label: 'Repeat',
    editable: true,
    type: 'select',
    // TODO: Handle repeats in backend, rather than specifying the repeat interval in frontend
    typeOptions: [
      {
        label: 'Every Day',
        value: 'daily',
      },
      {
        label: 'Every 1 Week',
        value: 'weekly',
      },
      {
        label: 'Every 2 Weeks',
        value: 'fortnightly',
      },
    ],
  },
});

export const studentUserToAttendee = (studentUser: ShortStudent): Attendee => ({
  id: studentUser.studentId,
  firstName: studentUser.firstName,
  lastName: studentUser.lastName,
});

export enum TutorialUserType {
  Admin = 'admin',
  Teacher = 'teacher',
  Host = 'host',
}

export const getStaffTutorialState = (
  tutorial: Tutorial,
  user: TutorialUserType,
  deleteButton: Button,
  attendeesButton: Button,
  saveButton: Button,
  joinButton: Button): EventState => {
  const startDatetime = getStartDatetime(tutorial.startDatetimeIdentifier);

  const now = dayjs();

  const attendeeCountString = `${tutorial.attendees.length} / ${tutorial.capacity}`;

  const imminentState: EventState = {
    state: 'imminent',
    buttons: [
      {
        ...deleteButton,
        disabled: true,
      },
      attendeesButton,
      joinButton,
    ],
    status: {
      text: 'Live',
      color: 'error',
      icon: <RadioButtonCheckedIcon fontSize="small" />,
    },
  };

  const editableState: EventState = {
    state: 'editable',
    buttons: [
      deleteButton,
      attendeesButton,
      saveButton,
    ],
    status: {
      text: attendeeCountString,
      color: now.isAfter(addDays(startDatetime, -1))
        ? 'gray' : 'darkPrimary',
      icon: <GroupIcon fontSize="small" />,
    },
  };

  const cancelledState: EventState = {
    state: 'cancelled',
    status: {
      text: 'Cancelled',
      color: 'gray',
      icon: <EventBusyIcon fontSize="small" />,
    },
  };

  const emptyState: EventState = {
    state: 'empty',
    buttons: [
      {
        ...attendeesButton,
        label: 'Add Attendees',
      },
    ],
    status: {
      text: 'Empty',
      color: 'gray',
      icon: <EventBusyIcon fontSize="small" />,
    },
  };

  const completedState: EventState = {
    state: 'completed',
    buttons: [
      {
        ...deleteButton,
        disabled: true,
      },
      attendeesButton,
    ],
    status: {
      text: 'Completed',
      color: 'gray',
      icon: <EventAvailableIcon fontSize="small" />,
    },
  };

  const readOnlyState: EventState = {
    state: 'readOnly',
    status: {
      text: attendeeCountString,
      color: 'gray',
      icon: <VisibilityIcon fontSize="small" />,
    },
  };

  const readOnlyHostState: EventState = {
    state: 'readOnlyHost',
    buttons: [
      {
        ...deleteButton,
        disabled: true,
      },
      attendeesButton,
    ],
    status: {
      text: attendeeCountString,
      color: 'darkPrimary',
      icon: <GroupIcon fontSize="small" />,
    },
  };

  if (user === TutorialUserType.Admin) return editableState;

  // If the user is an attendee or host
  if (user === TutorialUserType.Host) {
    // If tutorial is too soon to be booked
    if (now.isAfter(addDays(startDatetime, 1))) {
      if (tutorial.attendees.length === 0) {
        if (now.isAfter(startDatetime)) {
          return cancelledState;
        }
        return emptyState;
      }

      // If tutorial is 5 mins past finished
      if ((now.isAfter(startDatetime.add(tutorial.duration + 5, 'minute')))) {
        return completedState;
      }
      // If tutorial is 5 mins before starting
      if ((now.isAfter(startDatetime.subtract(5, 'minute')))) {
        return imminentState;
      }

      return readOnlyHostState;
    }
    return editableState;
  }

  return readOnlyState;
};
